import { useEffect, useRef, useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertTriangle, Eye, History, MailOpen, RefreshCw, Search, Users } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import {
  MESSAGE_GROUPS,
  SEND_STATUS_LABEL,
  displayName,
  historyTotals,
  isSelectable,
  type MessageSection,
  type MessageType,
  type RecipientUser,
  type RecipientsResponse,
  type SendHistoryRow,
} from "@/lib/message-centre";

export async function getJson<T>(url: string): Promise<T> {
  const r = await fetch(url, { credentials: "include" });
  if (!r.ok) throw new Error(`${r.status}: ${(await r.text().catch(() => "")) || r.statusText}`);
  return r.json() as Promise<T>;
}

export function errorText(err: unknown): string {
  return err instanceof Error ? err.message.replace(/^\d+:\s*/, "").replace(/^\{"message":"(.*)"\}$/, "$1") : "";
}

export function formatDate(d: string | null | undefined) {
  if (!d) return "-";
  return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export function formatShortDate(d: string | null | undefined) {
  if (!d) return "-";
  return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

// ── Authoritative server preview ─────────────────────────────────────────────

export function ServerEmailPreview({
  title, sections, messageType, recipientFirstName, height = 520, onReadyChange,
}: {
  title: string;
  sections: MessageSection[];
  messageType: MessageType;
  recipientFirstName?: string;
  height?: number;
  onReadyChange?: (ready: boolean) => void;
}) {
  const current = JSON.stringify({ title, sections, messageType, recipientFirstName: recipientFirstName || undefined });
  const payload = useDebounced(current, 600);
  const { data, isLoading, isError, error, refetch, isFetching, isPlaceholderData } = useQuery<{ html: string }>({
    queryKey: ["/api/admin/messages/preview", payload],
    queryFn: async () => {
      const res = await apiRequest("POST", "/api/admin/messages/preview", JSON.parse(payload));
      return res.json();
    },
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
  // Ready only when the rendered HTML is a successful response for exactly the current content.
  const ready = !!data && !isError && !isFetching && !isPlaceholderData && payload === current;
  const readyRef = useRef(onReadyChange);
  readyRef.current = onReadyChange;
  useEffect(() => { readyRef.current?.(ready); }, [ready]);
  useEffect(() => () => readyRef.current?.(false), []);

  if (isLoading) return <Skeleton className="w-full rounded-lg" style={{ height }} data-testid="preview-loading" />;
  if (isError || !data) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed text-center p-6" style={{ height }} data-testid="preview-error">
        <AlertTriangle className="h-6 w-6 text-destructive" />
        <p className="text-sm font-medium">Preview could not be rendered</p>
        <p className="text-xs text-muted-foreground max-w-xs">{errorText(error) || "The server did not return a preview."}</p>
        <Button size="sm" variant="outline" className="gap-1.5" onClick={() => refetch()} data-testid="button-retry-preview">
          <RefreshCw className="h-3.5 w-3.5" /> Retry
        </Button>
      </div>
    );
  }
  return (
    <div className="relative">
      {isFetching && <span className="absolute right-2 top-2 rounded bg-background/90 px-1.5 py-0.5 text-[10px] text-muted-foreground border">Updating</span>}
      {/* Empty sandbox: no scripts, no same-origin access. */}
      <iframe srcDoc={data.html} title="Email preview" sandbox="" className="w-full border rounded-lg bg-muted" style={{ height }} data-testid="iframe-email-preview" />
    </div>
  );
}

export function PreviewCard(props: { title: string; sections: MessageSection[]; messageType: MessageType }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2"><Eye className="h-4 w-4" />Email preview</CardTitle>
        <CardDescription className="text-xs">Rendered by the server with the same template used for sending</CardDescription>
      </CardHeader>
      <CardContent><ServerEmailPreview {...props} /></CardContent>
    </Card>
  );
}

// ── Recipients ───────────────────────────────────────────────────────────────

function OutreachCell({ u, type }: { u: RecipientUser; type: MessageType }) {
  const legacySent = type === "first_quote" ? u.firstQuoteNudgeEmailSentAt : null;
  const sent = u.lastMessageSentAt ?? legacySent;
  if (!sent) return <span className="text-xs text-muted-foreground">None</span>;
  const opened = u.lastMessageOpenedAt ?? (type === "first_quote" ? u.firstQuoteNudgeEmailOpenedAt : null);
  return (
    <div className="flex flex-col gap-0.5 text-xs">
      <span>Last sent {formatShortDate(sent)}</span>
      <span className={opened ? "text-emerald-700 dark:text-emerald-400" : "text-muted-foreground"}>
        {opened ? `Recorded open ${formatShortDate(opened)}` : "No recorded open"}
      </span>
    </div>
  );
}

export function RecipientsPanel({
  group, selection, blockedIds, onToggle, onSelectMany, onDeselectMany, onPrune, onClear, action,
}: {
  group: MessageType;
  selection: Record<string, RecipientUser>;
  blockedIds?: Set<string>;
  onToggle: (u: RecipientUser) => void;
  onSelectMany: (u: RecipientUser[]) => void;
  onDeselectMany: (ids: string[]) => void;
  onPrune: (ids: string[]) => void;
  onClear: () => void;
  action?: React.ReactNode;
}) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchInput, setSearchInput] = useState("");
  const search = useDebounced(searchInput, 300);
  useEffect(() => { setPage(1); }, [search]);

  const { data, isLoading, isError, refetch } = useQuery<RecipientsResponse>({
    queryKey: ["/api/admin/messages/recipients", group, page, pageSize, search],
    queryFn: async () => {
      const d = await getJson<RecipientsResponse>(`/api/admin/messages/recipients?type=${group}&page=${page}&pageSize=${pageSize}&search=${encodeURIComponent(search)}`);
      return d && Array.isArray(d.users) ? d : { users: [], total: 0 };
    },
    placeholderData: keepPreviousData,
  });

  const users = data?.users ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const blocked = blockedIds ?? new Set<string>();
  const selectable = users.filter(u => isSelectable(u) && !blocked.has(u.id));
  const allSelected = selectable.length > 0 && selectable.every(u => selection[u.id]);
  const selectedCount = Object.keys(selection).length;

  // Stale selections (user opted out / already contacted) are dropped as soon
  // as fresh data reveals it. The server rechecks everything again on send.
  const pruneRef = useRef(onPrune);
  pruneRef.current = onPrune;
  const blockedKey = Array.from(blocked).join(",");
  useEffect(() => {
    const ids = users.filter(u => !isSelectable(u) || blocked.has(u.id)).map(u => u.id);
    if (ids.length) pruneRef.current(ids);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, blockedKey]);

  return (
    <Card data-testid={`panel-recipients-${group}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <CardTitle className="text-sm flex items-center gap-2"><Users className="h-4 w-4" />Audience</CardTitle>
            <CardDescription className="text-xs mt-0.5 max-w-md">{MESSAGE_GROUPS[group].audience} Eligibility and opt-outs are checked again by the server at send time.</CardDescription>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search className="h-3.5 w-3.5 text-muted-foreground absolute left-2 top-1/2 -translate-y-1/2" />
              <Input value={searchInput} onChange={e => setSearchInput(e.target.value)} placeholder="Search name, email, company" className="h-8 text-xs w-56 pl-7" data-testid="input-recipient-search" />
            </div>
            <Select value={String(pageSize)} onValueChange={v => { setPageSize(Number(v)); setPage(1); }}>
              <SelectTrigger className="h-8 text-xs w-16" data-testid="select-page-size"><SelectValue /></SelectTrigger>
              <SelectContent>{[10, 25, 50, 100].map(n => <SelectItem key={n} value={String(n)} className="text-xs">{n}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <div className="p-4 space-y-2">{[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-9 w-full" />)}</div>
        ) : isError ? (
          <div className="p-8 text-center space-y-2">
            <p className="text-sm text-muted-foreground">Could not load recipients.</p>
            <Button size="sm" variant="outline" onClick={() => refetch()} data-testid="button-retry-recipients">Retry</Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10 pl-4">
                    <Checkbox
                      checked={allSelected}
                      disabled={selectable.length === 0}
                      onCheckedChange={() => allSelected ? onDeselectMany(selectable.map(u => u.id)) : onSelectMany(selectable)}
                      data-testid="checkbox-select-page"
                    />
                  </TableHead>
                  <TableHead>Recipient</TableHead>
                  <TableHead className="hidden md:table-cell">Company</TableHead>
                  <TableHead className="hidden sm:table-cell">Signed up</TableHead>
                  <TableHead className="hidden lg:table-cell">Plan</TableHead>
                  <TableHead>Prior outreach</TableHead>
                  <TableHead className="text-right pr-4">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="py-10 text-center">
                      <p className="text-sm font-medium">{search ? "No matches" : "Nobody in this audience right now"}</p>
                      <p className="text-xs text-muted-foreground mt-1">{search ? "Try a different search." : "Users appear here as soon as they match the audience rules."}</p>
                    </TableCell>
                  </TableRow>
                ) : users.map(u => {
                  const isBlocked = blocked.has(u.id);
                  const can = isSelectable(u) && !isBlocked;
                  return (
                    <TableRow key={u.id} className={can ? "" : "opacity-55"} data-testid={`row-recipient-${u.id}`}>
                      <TableCell className="pl-4">
                        <Checkbox checked={can && !!selection[u.id]} disabled={!can} onCheckedChange={() => can && onToggle(u)} data-testid={`checkbox-recipient-${u.id}`} />
                      </TableCell>
                      <TableCell>
                        <div className="text-sm font-medium leading-tight">{displayName(u) || "No name"}</div>
                        <div className="text-xs font-mono text-muted-foreground">{u.email ?? "No email address"}</div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-xs">{u.companyName || "-"}</TableCell>
                      <TableCell className="hidden sm:table-cell text-xs tabular-nums">{formatShortDate(u.createdAt)}</TableCell>
                      <TableCell className="hidden lg:table-cell text-xs capitalize">{u.subscriptionStatus || "trial"}</TableCell>
                      <TableCell><OutreachCell u={u} type={group} /></TableCell>
                      <TableCell className="text-right pr-4">
                        {u.emailUpdatesOptOut ? <Badge variant="outline" className="text-[10px]" title="Opted out of all Message Centre emails">Opted Out</Badge>
                          : !u.email ? <Badge variant="outline" className="text-[10px]">No email</Badge>
                          : isBlocked ? <Badge variant="secondary" className="text-[10px]">Already sent</Badge>
                          : <span className="text-[11px] text-muted-foreground">Eligible</span>}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
        <div className="flex items-center justify-between px-4 py-3 border-t flex-wrap gap-3">
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span className="font-semibold text-foreground tabular-nums" data-testid="text-selected-count">{selectedCount} selected</span>
            {selectedCount > 0 && <button type="button" className="underline underline-offset-2" onClick={onClear} data-testid="button-clear-selection">Clear</button>}
            <span className="tabular-nums">Page {page} of {totalPages} ({total} total)</span>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" className="h-7 text-xs" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="button-prev-page">Prev</Button>
            <Button size="sm" variant="outline" className="h-7 text-xs" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="button-next-page">Next</Button>
            {action}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Send history ─────────────────────────────────────────────────────────────

const STATUS_CLASS: Record<string, string> = {
  accepted: "border-emerald-300 text-emerald-800 bg-emerald-50 dark:bg-emerald-950 dark:text-emerald-300",
  sending: "border-sky-300 text-sky-800 bg-sky-50 dark:bg-sky-950 dark:text-sky-300",
  failed: "border-red-300 text-red-800 bg-red-50 dark:bg-red-950 dark:text-red-300",
  uncertain: "border-amber-300 text-amber-800 bg-amber-50 dark:bg-amber-950 dark:text-amber-300",
};

export function SendHistoryTable({ history, isLoading, isError, onRetry }: { history: SendHistoryRow[]; isLoading: boolean; isError?: boolean; onRetry?: () => void }) {
  const totals = historyTotals(history);
  return (
    <Card>
      <CardHeader className="pb-3 space-y-2">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <CardTitle className="text-sm font-medium flex items-center gap-2"><MailOpen className="h-4 w-4" />Send history</CardTitle>
          {!isLoading && !isError && <span className="text-xs text-muted-foreground tabular-nums" data-testid="text-history-totals">
            {totals.opened} of {totals.accepted} opened ({totals.openRate}%)
          </span>}
        </div>
        <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-muted-foreground">
          {totals.failed > 0 && <span><b className="text-foreground tabular-nums">{totals.failed}</b> failed</span>}
          {totals.uncertain > 0 && <span><b className="text-foreground tabular-nums">{totals.uncertain}</b> uncertain, not retried automatically</span>}
        </div>
        <p className="text-[11px] text-muted-foreground">Opens are based on email tracking and may not reflect readership. Sent means accepted by the email server, not confirmed inbox delivery.</p>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <div className="p-4 space-y-2">{[1, 2].map(i => <Skeleton key={i} className="h-8 w-full" />)}</div>
        ) : isError ? (
          <div className="p-8 text-center space-y-2">
            <p className="text-sm text-muted-foreground">Could not load send history.</p>
            <Button size="sm" variant="outline" onClick={onRetry} data-testid="button-retry-history">Retry</Button>
          </div>
        ) : history.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">Nothing sent for this message yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="pl-5 text-xs font-medium">Recipient</TableHead>
                  <TableHead className="text-xs font-medium">Sent</TableHead>
                  <TableHead className="pr-5 text-right text-xs font-medium">Opened</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.map(h => {
                  const status = h.status ?? "accepted";
                  return (
                    <TableRow key={h.id} data-testid={`row-send-${h.id}`}>
                      <TableCell className="pl-5 py-4">
                        <div className="text-sm font-normal break-all">{h.recipientEmail}</div>
                        <div className="text-xs text-muted-foreground">{displayName(h) || "No name"}</div>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground tabular-nums">
                        {status === "accepted" ? <span className="whitespace-nowrap" title={formatDate(h.sentAt)}>{formatShortDate(h.sentAt)}</span> :
                          <Badge variant="outline" className={`text-[10px] font-normal ${STATUS_CLASS[status] ?? ""}`}>{SEND_STATUS_LABEL[status] ?? status}</Badge>}
                        {h.error && <div className="text-[11px] text-destructive mt-1 max-w-[220px] truncate" title={h.error}>{h.error}</div>}
                      </TableCell>
                      <TableCell className="pr-5 text-right text-xs tabular-nums">
                        {h.openedAt ? <Badge className="gap-1 whitespace-nowrap text-[10px] font-normal" title={`First recorded open: ${formatDate(h.openedAt)}`}>
                          <MailOpen className="h-3 w-3" />{formatShortDate(h.openedAt)}
                        </Badge> : status === "accepted" ? <Badge variant="outline" className="whitespace-nowrap text-[10px] font-normal text-muted-foreground" title="No recorded open; tracking images may be blocked.">Not opened</Badge> : <span aria-label="No recorded open">—</span>}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Legacy first-quote history ───────────────────────────────────────────────

export function LegacyFirstQuoteHistory() {
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const search = useDebounced(searchInput, 300);
  useEffect(() => { setPage(1); }, [search]);
  const pageSize = 25;
  const { data, isLoading, isError, refetch } = useQuery<RecipientsResponse>({
    queryKey: ["/api/admin/messages/legacy-first-quote", page, search],
    queryFn: async () => {
      const d = await getJson<RecipientsResponse>(`/api/admin/messages/legacy-first-quote?page=${page}&pageSize=${pageSize}&search=${encodeURIComponent(search)}`);
      return d && Array.isArray(d.users) ? d : { users: [], total: 0 };
    },
    placeholderData: keepPreviousData,
  });
  const users = data?.users ?? [];
  const totalPages = Math.max(1, Math.ceil((data?.total ?? 0) / pageSize));
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="text-sm flex items-center gap-2"><History className="h-4 w-4" />Legacy first quote nudges</CardTitle>
            <CardDescription className="text-xs mt-0.5 max-w-lg">Sent by the old fixed First quote button in All Users. The original message content was not stored, so only send, open and PDF click evidence is shown. Includes users who have since created quotes.</CardDescription>
          </div>
          <div className="relative">
            <Search className="h-3.5 w-3.5 text-muted-foreground absolute left-2 top-1/2 -translate-y-1/2" />
            <Input value={searchInput} onChange={e => setSearchInput(e.target.value)} placeholder="Search" className="h-8 text-xs w-48 pl-7" data-testid="input-legacy-search" />
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <div className="p-4 space-y-2">{[1, 2, 3].map(i => <Skeleton key={i} className="h-8 w-full" />)}</div>
        ) : isError ? (
          <div className="p-8 text-center"><Button size="sm" variant="outline" onClick={() => refetch()} data-testid="button-retry-legacy">Retry</Button></div>
        ) : users.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">No legacy first quote nudges recorded.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="pl-4">Recipient</TableHead>
                  <TableHead>Sent</TableHead>
                  <TableHead>Open</TableHead>
                  <TableHead className="pr-4">PDF click</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map(u => (
                  <TableRow key={u.id} data-testid={`row-legacy-${u.id}`}>
                    <TableCell className="pl-4">
                      <div className="text-sm">{displayName(u) || "No name"}</div>
                      <div className="text-xs font-mono text-muted-foreground">{u.email ?? "Deleted or anonymised"}</div>
                    </TableCell>
                    <TableCell className="text-xs tabular-nums">{formatDate(u.firstQuoteNudgeEmailSentAt)}</TableCell>
                    <TableCell className="text-xs tabular-nums">{u.firstQuoteNudgeEmailOpenedAt ? `Recorded open ${formatDate(u.firstQuoteNudgeEmailOpenedAt)}` : <span className="text-muted-foreground">No recorded open</span>}</TableCell>
                    <TableCell className="pr-4 text-xs tabular-nums">{u.firstQuoteNudgeEmailClickedAt ? formatDate(u.firstQuoteNudgeEmailClickedAt) : <span className="text-muted-foreground">None recorded</span>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t">
          <span className="text-xs text-muted-foreground mr-auto tabular-nums">Page {page} of {totalPages}</span>
          <Button size="sm" variant="outline" className="h-7 text-xs" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="button-legacy-prev">Prev</Button>
          <Button size="sm" variant="outline" className="h-7 text-xs" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="button-legacy-next">Next</Button>
        </div>
      </CardContent>
    </Card>
  );
}
